import { useState, useCallback } from "react";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { UserRole } from "@/generated/types";
import { useUpdateUser } from "../../../hooks/useUpdateUser";
import type { User } from "@/generated/types";

interface EditUserDialogProps {
  user: User;
  onSuccess: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "Administrador",
  CLIENT: "Cliente",
  COLLABORATOR: "Colaborador",
};

export default function EditUserDialog({ user, onSuccess }: EditUserDialogProps) {
  const [email, setEmail] = useState(user.email);
  const [fullName, setFullName] = useState(user.fullName);
  const [role, setRole] = useState<string>(user.role);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { updateUser, loading } = useUpdateUser({
    onCompleted: () => {
      toast.success("Usuário atualizado com sucesso.");
      setFormErrors({});
      onSuccess();
    },
    onError: (error: any) => {
      const graphQLError = error.graphQLErrors?.[0];
      if (graphQLError?.extensions?.field_errors) {
        const fieldErrors: Record<string, string> = {};
        Object.entries(graphQLError.extensions.field_errors).forEach(([key, val]) => {
          fieldErrors[key] = String(val);
        });
        setFormErrors(fieldErrors);
      }
    },
  });

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!email.trim()) {
      errors.email = "E-mail é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Por favor, insira um endereço de e-mail válido";
    }
    if (!fullName.trim()) {
      errors.fullName = "Nome completo é obrigatório";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email, fullName]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    updateUser(user.id, undefined, role, fullName, email);
  }, [validate, updateUser, user, role, fullName, email]);

  return (
    <Dialog>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Editar usuário"
              className="h-8 w-8 p-0 text-primary-600 dark:text-primary-400 hover:text-primary-950 dark:hover:text-white"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Editar usuário</TooltipContent>
      </Tooltip>
      <DialogContent maxWidth="max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar Usuário</DialogTitle>
          <DialogDescription>
            Atualizar detalhes e função do usuário.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="space-y-2">
            <Label htmlFor="edit-email" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              E-mail
            </Label>
            <Input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {formErrors.email && <p className="text-xs text-red-600 dark:text-red-400">{formErrors.email}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-fullName" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Nome Completo
            </Label>
            <Input
              id="edit-fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {formErrors.fullName && <p className="text-xs text-red-600 dark:text-red-400">{formErrors.fullName}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Função
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-none border-primary-200 dark:border-primary-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(UserRole).map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r] || r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="rounded-none uppercase tracking-widest text-xs border-primary-200 dark:border-primary-800"
              >
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-none uppercase tracking-widest text-xs"
            >
              {loading ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
